/**
 * ComponentSkeleton - Loading fallback for lazy-loaded components
 * Used with React.Suspense to show graceful loading states
 */

interface ComponentSkeletonProps {
  variant?: "section" | "card" | "compact";
  className?: string;
}

export const ComponentSkeleton = ({ 
  variant = "section",
  className = "" 
}: ComponentSkeletonProps) => {
  if (variant === "compact") {
    return (
      <div 
        className={`animate-pulse py-8 ${className}`}
        role="status"
        aria-live="polite"
        aria-label="Loading content"
      >
        <div className="h-20 bg-muted/50 rounded-lg" />
      </div>
    );
  }

  if (variant === "card") {
    return (
      <div 
        className={`animate-pulse space-y-4 p-6 ${className}`}
        role="status"
        aria-live="polite"
        aria-label="Loading content"
      >
        <div className="h-8 bg-muted/50 rounded w-3/4" />
        <div className="h-4 bg-muted/50 rounded w-full" />
        <div className="h-4 bg-muted/50 rounded w-5/6" />
      </div>
    );
  }

  // Default section skeleton
  return (
    <section 
      className={`relative py-16 md:py-24 ${className}`}
      role="status"
      aria-live="polite"
      aria-label="Loading section"
    >
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="animate-pulse space-y-8">
          {/* Skeleton header */}
          <div className="text-center space-y-4">
            <div className="h-12 bg-muted/50 rounded-lg w-2/3 mx-auto" />
            <div className="h-6 bg-muted/50 rounded w-3/4 mx-auto" />
          </div>

          {/* Skeleton content grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-3 p-6 border border-border/40 rounded-lg">
                <div className="h-6 bg-muted/50 rounded w-3/4" />
                <div className="h-4 bg-muted/50 rounded w-full" />
                <div className="h-4 bg-muted/50 rounded w-5/6" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
